import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Loader2, Check, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Card = ({ children }) => (
  <div
    className="max-w-md w-full mx-auto bg-white border border-slate-200 rounded-2xl shadow-xl shadow-emerald-500/10 p-8 md:p-10"
    data-testid="unsub-card"
  >
    {children}
  </div>
);

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const email = (params.get("email") || "").toLowerCase().trim();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  // status: "active" | "paused" | "not_found" | "reactivated" | "paused-now"
  const [status, setStatus] = useState("active");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!email || !token) {
        setError("Missing email or token in link.");
        setLoading(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/briefing/status`, { params: { email, token } });
        if (!cancelled) setStatus(data.status);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Invalid or expired link.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [email, token]);

  const doPause = async () => {
    setWorking(true);
    setError("");
    try {
      await axios.post(`${API}/briefing/unsubscribe`, { email, token });
      setStatus("paused-now");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setWorking(false);
    }
  };

  const doResubscribe = async () => {
    setWorking(true);
    setError("");
    try {
      await axios.post(`${API}/briefing/resubscribe`, { email, token });
      setStatus("reactivated");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      data-testid="unsubscribe-page"
      className="min-h-screen bg-slate-50 px-4 py-16 md:py-24"
      style={{ fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif" }}
    >
      <div className="max-w-md mx-auto mb-6">
        <Link
          to="/"
          data-testid="unsub-back-home"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </div>

      <Card>
        <div className="inline-flex items-center gap-2 text-emerald-700 text-[11px] font-bold tracking-[0.2em] uppercase">
          <ShieldCheck className="w-4 h-4" /> Profit Briefing
        </div>

        {loading && (
          <div data-testid="unsub-loading" className="mt-6 flex items-center gap-2 text-slate-600">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your subscription…
          </div>
        )}

        {!loading && error && (
          <div data-testid="unsub-error" className="mt-5">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              We couldn’t verify this link.
            </h1>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
              {error} Please request a fresh unsubscribe link from the most recent briefing email.
            </p>
          </div>
        )}

        {!loading && !error && status === "active" && (
          <div data-testid="unsub-confirm">
            <h1 className="mt-5 text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              Pause your daily briefing?
            </h1>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
              You’re currently receiving the daily 7&nbsp;AM Profit Briefing at{" "}
              <span className="font-semibold text-slate-900">{email}</span>. You can pause
              it anytime — we’ll keep your settings so you can reactivate in one click.
            </p>
            <button
              data-testid="unsub-confirm-btn"
              onClick={doPause}
              disabled={working}
              className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-60 transition"
            >
              {working ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Pause briefings
            </button>
          </div>
        )}

        {!loading && !error && (status === "paused" || status === "paused-now") && (
          <div data-testid="unsub-paused">
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">
              <Check className="w-3.5 h-3.5" />
              {status === "paused-now" ? "Briefings paused" : "Currently paused"}
            </div>
            <h1 className="mt-4 text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              {status === "paused-now"
                ? "You’re all set. No more morning emails."
                : "Your briefings are currently paused."}
            </h1>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
              We’ve stopped sending daily Profit Briefings to{" "}
              <span className="font-semibold text-slate-900">{email}</span>. Change your
              mind? Reactivate in one click — we’ll resume at your original 7&nbsp;AM local time.
            </p>
            <button
              data-testid="unsub-reactivate-btn"
              onClick={doResubscribe}
              disabled={working}
              className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 disabled:opacity-60 transition shadow-lg shadow-emerald-500/30"
            >
              {working ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Reactivate my briefing
            </button>
          </div>
        )}

        {!loading && !error && status === "reactivated" && (
          <div data-testid="unsub-reactivated">
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">
              <Check className="w-3.5 h-3.5" /> Welcome back
            </div>
            <h1 className="mt-4 text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              We’ve reactivated your briefing.
            </h1>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
              Tomorrow at 7&nbsp;AM your local time, you’ll get the next Profit Briefing
              for <span className="font-semibold text-slate-900">{email}</span> — orders, revenue,
              ad spend, ROAS, and the top 1-click move.
            </p>
          </div>
        )}

        {!loading && !error && status === "not_found" && (
          <div data-testid="unsub-not-found">
            <h1 className="mt-5 text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              No active briefing found.
            </h1>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
              We don’t have an active subscription for{" "}
              <span className="font-semibold text-slate-900">{email}</span>. You’re all clear.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Unsubscribe;
